import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ChevronRight, Minus, Plus, Heart, Check, ChevronDown, Star, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { BenefitBar } from "@/components/BenefitBar";
import { ProductReviews } from "@/components/ProductReviews";

const money = (c) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);
const tid = (s) => String(s).replace(/\s+/g, "-").replace(/[^a-zA-Z0-9\-_.®]/g, "");
const stripHtml = (html) => {
  if (!html) return "";
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent || el.innerText || "").replace(/\n{3,}/g, "\n\n").trim();
};

function Accordion({ title, children, open: o }) {
  const [open, setOpen] = useState(!!o);
  return (
    <div className="border-b border-charcoal/12">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full py-3.5 text-left" data-testid={`acc-${title.replace(/\s/g, "-")}`}>
        <span className="font-medium text-charcoal text-sm">{title}</span>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-4 text-sm text-charcoal/65 leading-relaxed">{children}</div>}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [sel, setSel] = useState({});  // dynamic option groups: group name -> value id
  const [qty, setQty] = useState(1);

  const loadProduct = () => api.shopProduct(slug).then((d) => {
    setP(d); setImg(0);
    setColor(d.colors?.[0]?.name || null); setSize(d.sizes?.[0] || null);
    const groups = d.option_groups || [];
    if (groups.length) {
      // preselect greedily in display order (e.g. XS before 5XL), preferring available variants,
      // so the page opens on the first purchasable combination
      const init = {};
      const pool = (preferAvailable) => d.variants.filter((v) => !preferAvailable || v.is_available !== false);
      groups.forEach((g) => {
        const pick = (vs) => g.values.find((val) => vs.some((v) =>
          groups.every((gg) => init[gg.name] === undefined || init[gg.name] == null || (v.options || []).some((o) => o.group === gg.name && o.value_id === init[gg.name]))
          && (v.options || []).some((o) => o.group === g.name && o.value_id === val.id)));
        init[g.name] = (pick(pool(true)) || pick(pool(false)) || {}).id ?? null;
      });
      setSel(init);
    }
    if (d.seo_title || d.title) document.title = `${d.seo_title || d.title} · HomeRange Goods`;
  }).catch(() => setP(false));

  useEffect(() => {
    window.scrollTo(0, 0);
    loadProduct();
  }, [slug]);

  // Switch the gallery to the resolved variant's specific image when the selection changes
  useEffect(() => {
    if (!p || !(p.option_groups || []).length) return;
    const v = p.variants.find((x) => p.option_groups.every((g) => (x.options || []).some((o) => o.group === g.name && o.value_id === sel[g.name])));
    if (!v) return;
    const i = p.images.findIndex((im) => (im.variant_ids || []).includes(v.id));
    if (i >= 0) setImg(i);
  }, [sel, p]);

  if (p === null) return <div className="min-h-screen grid place-items-center bg-paper"><Loader2 className="animate-spin text-blaze" /></div>;
  if (!p) return <div className="bg-paper min-h-screen"><SiteHeader /><p className="text-center py-32 text-charcoal/60">Product not found.</p><Footer /></div>;

  const groups = p.option_groups || [];
  const dynamic = groups.length > 0;
  // A variant matches only when EVERY group selection maps to it — the resolved variant carries
  // the exact Printify variant id used at checkout; fulfillment never infers from labels.
  const matchAll = (v, selection) => groups.every((g) => selection[g.name] == null || (v.options || []).some((o) => o.group === g.name && o.value_id === selection[g.name]));
  const variant = dynamic
    ? (groups.every((g) => sel[g.name] != null) ? p.variants.find((v) => matchAll(v, sel)) || null : null)
    : (p.variants.find((v) => (!p.colors.length || v.color === color) && (!p.sizes.length || v.size === size)) || p.variants[0]);
  const price = variant?.price ?? p.effective_price;
  const available = dynamic ? (variant ? variant.is_available !== false : false) : (variant ? variant.is_available !== false : true);

  // State of one option value given the other selections: "ok" | "soldout" | "disabled" (combination doesn't exist)
  const optionState = (g, valueId) => {
    const matching = p.variants.filter((v) => matchAll(v, { ...sel, [g.name]: null }) && (v.options || []).some((o) => o.group === g.name && o.value_id === valueId));
    if (!matching.length) return "disabled";
    return matching.every((v) => v.is_available === false) ? "soldout" : "ok";
  };

  const select = (gname, vid) => {
    const next = { ...sel, [gname]: vid };
    // repair other groups whose current value no longer combines with the new selection
    groups.forEach((g) => {
      if (g.name === gname || next[g.name] == null) return;
      const ok = p.variants.some((v) => matchAll(v, { ...next, [g.name]: null }) && (v.options || []).some((o) => o.group === g.name && o.value_id === next[g.name]));
      if (!ok) {
        const first = g.values.find((val) => p.variants.some((v) => matchAll(v, { ...next, [g.name]: null }) && (v.options || []).some((o) => o.group === g.name && o.value_id === val.id)));
        next[g.name] = first ? first.id : null;
      }
    });
    setSel(next);
  };

  const addToCart = () => {
    if (!variant) { toast.error("Please choose your options"); return; }
    const label = dynamic
      ? groups.map((g) => (variant.options || []).find((o) => o.group === g.name)?.value).filter(Boolean).join(" · ")
      : [variant.color, variant.size].filter(Boolean).join(" · ");
    add({ product_id: p.id, variant_id: variant.id, quantity: qty, title: p.title,
          variant_title: label, price, image: p.images?.[img]?.src || p.images?.[0]?.src });
  };

  return (
    <div className="bg-paper min-h-screen" data-testid="product-page">
      <SiteHeader />
      <div className="max-w-[1300px] mx-auto px-5 sm:px-10 pt-6">
        <nav className="flex items-center gap-1.5 text-xs text-charcoal/45" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-blaze">Home</Link><ChevronRight size={12} />
          <Link to="/shop" className="hover:text-blaze">HomeRange Goods</Link>
          {p.category && <><ChevronRight size={12} /><span>{p.category.name}</span></>}
          <ChevronRight size={12} /><span className="text-charcoal/70">{p.title}</span>
        </nav>
      </div>

      <div className="max-w-[1300px] mx-auto px-5 sm:px-10 py-8 grid lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div className="flex gap-3">
          <div className="flex flex-col gap-2 shrink-0" data-testid="pd-thumbs">
            {p.images.map((im, i) => (
              <button key={i} onClick={() => setImg(i)} className={`w-16 h-16 border ${i === img ? "border-blaze" : "border-charcoal/15"} overflow-hidden`} data-testid={`pd-thumb-${i}`}>
                <img src={im.src} alt={im.alt} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div className="flex-1 aspect-square bg-bone overflow-hidden" data-testid="pd-main-image">
            {p.images[img] && <img src={p.images[img].src} alt={p.images[img].alt} className="w-full h-full object-cover" />}
          </div>
        </div>

        {/* Info */}
        <div>
          <p className="hr-overline text-blaze">HomeRange Goods</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-charcoal tracking-tight mt-2" data-testid="pd-title">{p.title}</h1>
          {p.subtitle && <p className="text-charcoal/55 mt-1">{p.subtitle}</p>}
          <div className="flex items-center gap-4 mt-4">
            <span className="text-2xl font-semibold text-charcoal" data-testid="pd-price">{p.sale_price ? <><span className="text-blaze">{money(p.sale_price)}</span> <span className="line-through text-charcoal/40 text-lg">{money(p.price)}</span></> : money(price)}</span>
            {p.review_count > 0
              ? <span className="flex items-center gap-1 text-sm text-charcoal/60"><Star size={14} className="fill-saddle text-saddle" /> {p.rating} ({p.review_count})</span>
              : <span className="text-xs text-charcoal/40">No reviews yet</span>}
          </div>
          {p.description && <p className="text-charcoal/70 mt-5 leading-relaxed text-sm whitespace-pre-line" data-testid="pd-description">{stripHtml(p.description)}</p>}

          {p.features?.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6" data-testid="pd-features">
              {p.features.map((f, i) => (
                <div key={i} className="text-center bg-bone py-3 px-2 border border-charcoal/8">
                  <Check size={16} className="mx-auto text-saddle" />
                  <p className="text-[11px] text-charcoal/70 mt-1.5 leading-tight">{f.title}</p>
                </div>
              ))}
            </div>
          )}

          {dynamic && groups.map((g) => (
            <div className="mt-6" key={g.name} data-testid={`pd-group-${tid(g.name)}`}>
              <p className="hr-overline text-charcoal/50 mb-2">{g.name}: <span className="text-charcoal normal-case tracking-normal" data-testid={`pd-group-${tid(g.name)}-selected`}>{g.values.find((v) => v.id === sel[g.name])?.title || "Choose"}</span></p>
              <div className="flex flex-wrap gap-2">
                {g.values.map((val) => {
                  const st = optionState(g, val.id);
                  const selected = sel[g.name] === val.id;
                  return val.hex ? (
                    <button key={val.id} disabled={st === "disabled"} onClick={() => select(g.name, val.id)}
                      title={val.title + (st === "soldout" ? " — sold out" : "")}
                      className={`w-8 h-8 rounded-full border-2 relative transition-transform hover:scale-110 ${selected ? "border-saddle ring-2 ring-saddle/30" : "border-charcoal/15"} ${st !== "ok" ? "opacity-40" : ""} disabled:cursor-not-allowed disabled:hover:scale-100`}
                      style={{ background: val.hex }} data-testid={`pd-option-${tid(g.name)}-${tid(val.title)}`} aria-label={val.title}>
                      {st === "soldout" && <X size={14} className="absolute inset-0 m-auto text-white drop-shadow" />}
                    </button>
                  ) : (
                    <button key={val.id} disabled={st === "disabled"} onClick={() => select(g.name, val.id)}
                      className={`min-w-[44px] px-3 py-2 text-sm border transition-colors ${selected ? "bg-forest text-bone border-forest" : "border-charcoal/20 text-charcoal/70 hover:border-forest"} ${st === "soldout" ? "opacity-50 line-through" : ""} disabled:opacity-30 disabled:cursor-not-allowed`}
                      data-testid={`pd-option-${tid(g.name)}-${tid(val.title)}`}>{val.title}</button>
                  );
                })}
              </div>
            </div>
          ))}

          {!dynamic && p.colors?.length > 0 && (
            <div className="mt-6">
              <p className="hr-overline text-charcoal/50 mb-2">Color: <span className="text-charcoal normal-case tracking-normal">{color}</span></p>
              <div className="flex gap-2">
                {p.colors.map((c) => <button key={c.name} onClick={() => setColor(c.name)} title={c.name} className={`w-8 h-8 rounded-full border-2 ${color === c.name ? "border-saddle" : "border-charcoal/15"}`} style={{ background: c.hex }} data-testid={`pd-color-${c.name}`} />)}
              </div>
            </div>
          )}
          {!dynamic && p.sizes?.length > 0 && (
            <div className="mt-5">
              <p className="hr-overline text-charcoal/50 mb-2">Size</p>
              <div className="flex flex-wrap gap-2">
                {p.sizes.map((s) => <button key={s} onClick={() => setSize(s)} className={`min-w-[44px] px-3 py-2 text-sm border ${size === s ? "bg-forest text-bone border-forest" : "border-charcoal/20 text-charcoal/70"}`} data-testid={`pd-size-${s}`}>{s}</button>)}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mt-7">
            <div className="flex items-center border border-charcoal/20">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3"><Minus size={15} /></button>
              <span className="px-3 text-sm" data-testid="pd-qty">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3 py-3"><Plus size={15} /></button>
            </div>
            <button onClick={addToCart} disabled={!available} className="flex-1 bg-blaze text-white py-3.5 font-semibold uppercase tracking-wide text-sm hover:brightness-95 transition-all disabled:opacity-50" data-testid="pd-add-to-cart">
              {dynamic && !variant ? "Select Options" : available ? "Add to Cart" : "Sold Out"}
            </button>
            <button onClick={() => toast.success("Saved to wishlist")} className="p-3.5 border border-charcoal/20 text-charcoal/60 hover:text-blaze" data-testid="pd-wishlist" aria-label="Add to wishlist"><Heart size={18} /></button>
          </div>

          <div className="mt-8">
            {(p.details?.length > 0) && (
              <Accordion title="Product Details" open>
                <ul className="space-y-1">{p.details.map((d, i) => <li key={i}><span className="text-charcoal/50">{d.label}:</span> {d.value}</li>)}</ul>
              </Accordion>
            )}
            <Accordion title="Shipping & Returns">{p.shipping_info || "Shipping is calculated at checkout."} <Link to="/returns" className="text-blaze font-medium underline underline-offset-2" data-testid="pd-returns-link">Start a return</Link>.</Accordion>
            <Accordion title="Care & Safety">{p.care || "Care details coming soon."}</Accordion>
          </div>
        </div>
      </div>

      {p.related?.length > 0 && (
        <section className="max-w-[1300px] mx-auto px-5 sm:px-10 py-12" data-testid="pd-related">
          <h2 className="font-serif text-3xl text-charcoal tracking-tight mb-6">You may also like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
            {p.related.map((r) => (
              <Link key={r.id} to={`/shop/${r.slug}`} className="group" data-testid={`related-${r.id}`}>
                <div className="aspect-square bg-bone overflow-hidden">{r.primary_image && <img src={r.primary_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />}</div>
                <p className="font-serif text-sm text-charcoal mt-2 leading-snug">{r.title}</p>
                <p className="text-charcoal/70 text-sm font-semibold">{money(r.effective_price)}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <ProductReviews product={p} onSubmitted={loadProduct} />

      <BenefitBar dark />
      <Footer />
    </div>
  );
}
